import React from 'react'

import EditIcon from '@material-ui/icons/Edit'
import {
    IconButton,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
} from '@material-ui/core'
import { ClassNameMap } from '@material-ui/styles'

import { currencyFormatter, formatScheduleDateInZone } from '../../helpers'
import { ScheduleRow } from './types'
import { Container, RenderRow, StyledProgressBar } from '../../components'
import { ScheduleReportPopOver } from '../ScheduleHome/ViewComponents/ScheduleReportPopover/ScheduleReportPopover'
import NumberFormat from 'react-number-format'
import { hasPermission } from '../../models/Users/services'
import { SEE_VENDOR_FINANCES, User } from '../../models'
import { useTimezone } from '../../contexts/timezone'

interface Props {
    row: ScheduleRow
    classes: ClassNameMap
    theme: Theme
    barStyle: React.CSSProperties
    barLabelStyle: React.CSSProperties
    cellStyle: React.CSSProperties
    workspaceUser?: User
    onClickRow?: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void
    setEditFormOpen?: (
        open: boolean,
        event?: React.MouseEvent<HTMLElement>,
    ) => void
}

export const ScheduleRowComponent: RenderRow<Props> = (props) => {
    const {
        row,
        classes,
        cellStyle,
        theme,
        barLabelStyle,
        barStyle,
        workspaceUser,
        setEditFormOpen,
    } = props

    const { currentTimezone, propertyTimezone } = useTimezone()
    const displayZone = propertyTimezone ?? currentTimezone

    const startDateLabel = formatScheduleDateInZone(
        row.startDate.value,
        displayZone,
    )

    const endDateLabel = row.endDate.value
        ? formatScheduleDateInZone(row.endDate.value, displayZone)
        : ''

    const schedule = row.schedule.value

    let completeBarValue = 0
    let approvedBarValue = 0
    let invoicedBarValue = 0

    if (schedule.assigned_count > 0) {
        completeBarValue =
            (schedule.complete_count / schedule.assigned_count) * 100
        approvedBarValue =
            (schedule.approved_count / schedule.assigned_count) * 100
        invoicedBarValue =
            (schedule.invoiced_count / schedule.assigned_count) * 100
    }

    return (
        <TableRow
            key={`SCHEDULE_ROW_${schedule.id}`}
            style={{ cursor: 'pointer' }}
            className={classes.hoverGrey200}
            onClick={props.onClickRow}
        >
            <TableCell style={cellStyle}>{row.nameAndId.value}</TableCell>
            <TableCell style={cellStyle}>
                {hasPermission(workspaceUser, SEE_VENDOR_FINANCES)
                    ? currencyFormatter.format(row.budget.value)
                    : ''}
            </TableCell>
            <TableCell align="center" style={cellStyle}>
                {startDateLabel}
            </TableCell>
            <TableCell align="center" style={cellStyle}>
                {endDateLabel}
            </TableCell>
            <TableCell align="center" style={cellStyle}>
                <NumberFormat
                    displayType="text"
                    value={row.assignments.value}
                    thousandSeparator
                />
            </TableCell>
            <TableCell align="left" style={cellStyle}>
                {row.inspection.value}
            </TableCell>

            {/* Progress Bars */}
            <TableCell align="center" style={{ height: '100%' }}>
                <Container direction="row" flex={1}>
                    {/* Labels */}
                    <Container
                        direction="column"
                        style={{
                            justifyContent: 'space-evenly',
                            marginRight: theme.spacing(1),
                        }}
                    >
                        <span style={barLabelStyle}>Complete</span>
                        <span style={barLabelStyle}>Approved</span>
                        <span style={barLabelStyle}>Invoiced</span>
                    </Container>

                    {/* Bars */}
                    <Container
                        direction="column"
                        flex={1}
                        style={{
                            justifyContent: 'space-evenly',
                        }}
                    >
                        <StyledProgressBar
                            customStyle={barStyle}
                            value={completeBarValue}
                            variant="determinate"
                            barColor={theme.palette.info.main}
                        />

                        <StyledProgressBar
                            customStyle={barStyle}
                            value={approvedBarValue}
                            variant="determinate"
                            barColor={theme.palette.secondary.main}
                        />

                        <StyledProgressBar
                            customStyle={barStyle}
                            value={invoicedBarValue}
                            variant="determinate"
                            barColor={theme.palette.primary.main}
                        />
                    </Container>
                </Container>
            </TableCell>

            {/* Action Buttons */}
            <TableCell align="center" style={cellStyle}>
                <Container
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                >
                    <ScheduleReportPopOver
                        schedule={schedule}
                        workspaceUser={workspaceUser as any}
                    />

                    <Tooltip title="Edit">
                        <IconButton
                            onClick={(e) => {
                                console.log('Edit button clicked!')
                                e.stopPropagation()
                                console.log(
                                    'Calling setEditFormOpen with event:',
                                    e,
                                )
                                setEditFormOpen?.(true, e)
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Container>
            </TableCell>
        </TableRow>
    )
}
